import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getClientDocuments,
  uploadClientDocument,
  deleteClientDocument,
  type ClientDocument,
} from '@/lib/api/clients';
import apiClient from '@/lib/api/client';
import { DocumentViewerModal } from '@/components/common/DocumentViewerModal';
import { ConfirmDialog } from '@/components/common';

interface ClientDocumentsSectionProps {
  clientId: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type: string, name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf' || ext === 'pdf') return 'picture_as_pdf';
  if (['doc', 'docx'].includes(ext)) return 'description';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'table_chart';
  if (['txt', 'log'].includes(ext)) return 'article';
  return 'description';
}

function isImage(type: string, name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return type.startsWith('image/') || ['jpg','jpeg','png','gif','webp','bmp','tiff','heic','heif'].includes(ext);
}

const ACCEPTED = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.heic,.heif,.mmdl,.mmdf,.stl,.obj,.glb,.gltf,.3mf';

const CATEGORIES = ['Contract', 'Insurance', 'Permit', 'Invoice', 'Photo', 'ID', 'Other'];

export const ClientDocumentsSection = ({ clientId }: ClientDocumentsSectionProps) => {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerDoc, setViewerDoc] = useState<ClientDocument | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [confirmPending, setConfirmPending] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const docs = await getClientDocuments(clientId);
      setDocuments(docs);
    } catch {
      console.error('Failed to fetch client documents');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    setError(null);
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadClientDocument(clientId, files[i], selectedCategory || undefined);
      }
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = (docId: number) => {
    setConfirmPending({
      message: 'Delete this document?',
      onConfirm: async () => {
        setConfirmPending(null);
        setError(null);
        try {
          await deleteClientDocument(clientId, String(docId));
          setDocuments(prev => prev.filter(d => d.id !== docId));
        } catch {
          setError('Failed to delete document');
        }
      },
    });
  };

  const handleDownload = async (doc: { id: number; name: string }) => {
    try {
      const res = await apiClient.get(`/clients/${clientId}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setError('Failed to download document');
    }
  };

  const visibleDocs = filterCategory === 'All'
    ? documents
    : documents.filter(d => d.category === filterCategory);

  const categories = ['All', ...Array.from(new Set(documents.map(d => d.category).filter(Boolean)))];

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="material-symbols-outlined animate-spin text-3xl text-gray-400">progress_activity</span>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-lg">error</span>
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      )}

      {/* Upload zone */}
      <div className="flex gap-3 items-start mb-4">
        <label
          htmlFor="client-dropzone-file"
          className={`flex flex-col items-center justify-center flex-1 h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
            dragActive ? 'border-primary bg-primary/10' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
          } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center py-4">
            {uploading ? (
              <>
                <span className="material-symbols-outlined animate-spin text-3xl text-primary">progress_activity</span>
                <p className="mt-1 text-sm text-gray-600">Uploading...</p>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-3xl text-gray-500">cloud_upload</span>
                <p className="mt-1 text-sm text-gray-600">
                  <span className="font-semibold">Click to upload</span> or drag & drop
                </p>
                <p className="text-xs text-gray-400">Images, PDF, Word, Excel (max 25 MB)</p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            id="client-dropzone-file"
            type="file"
            className="hidden"
            multiple
            accept={ACCEPTED}
            onChange={e => { if (e.target.files?.length) handleFiles(e.target.files); }}
          />
        </label>

        {/* Category selector for upload */}
        <div className="flex flex-col gap-1 shrink-0">
          <label className="text-xs font-medium text-gray-500">Category (optional)</label>
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">No category</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Filter by category */}
      {categories.length > 1 && (
        <div className="flex gap-2 flex-wrap mb-4">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat ?? 'All')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filterCategory === (cat ?? 'All')
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat ?? 'Uncategorised'}
            </button>
          ))}
        </div>
      )}

      {/* Document grid */}
      {visibleDocs.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {visibleDocs.map(doc => (
            <div key={doc.id} className="relative group">
              <div
                className="aspect-square w-full rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center cursor-pointer border border-gray-200"
                onClick={() => setViewerDoc(doc)}
              >
                {isImage(doc.type, doc.name) ? (
                  <img src={doc.url} alt={doc.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-400 p-2">
                    <span className="material-symbols-outlined text-5xl">{getFileIcon(doc.type, doc.name)}</span>
                    <span className="text-xs mt-1 uppercase font-mono">{doc.name.split('.').pop()}</span>
                  </div>
                )}
              </div>

              {doc.category && (
                <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-black/50 text-white">
                  {doc.category}
                </span>
              )}
              {/* CR #121: docs pulled in from the client's jobs are labelled */}
              {doc.job_title && (
                <span className="absolute bottom-1.5 left-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#C77D3F]/90 text-white truncate">
                  {doc.job_title}
                </span>
              )}

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 rounded-lg">
                <button
                  onClick={() => setViewerDoc(doc)}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                  title="View"
                >
                  <span className="material-symbols-outlined text-white text-xl">visibility</span>
                </button>
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                  title="Download"
                >
                  <span className="material-symbols-outlined text-white text-xl">download</span>
                </button>
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 transition-colors"
                  title="Delete"
                >
                  <span className="material-symbols-outlined text-white text-xl">delete</span>
                </button>
              </div>

              <p className="text-xs text-center mt-1 truncate text-gray-600" title={doc.name}>{doc.name}</p>
              {(typeof doc.size === 'number' && doc.size > 0) && (
                <p className="text-[10px] text-center text-gray-400">{formatFileSize(doc.size)}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400">
          <span className="material-symbols-outlined text-4xl mb-2 block opacity-40">folder_open</span>
          <p className="text-sm">No documents uploaded yet</p>
          <p className="text-xs mt-1">Upload contracts, permits, photos, or other files</p>
        </div>
      )}

      <DocumentViewerModal
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
        onDownload={handleDownload}
      />
      {confirmPending && (
        <ConfirmDialog
          message={confirmPending.message}
          onConfirm={confirmPending.onConfirm}
          onCancel={() => setConfirmPending(null)}
        />
      )}
    </>
  );
};
